import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  PharmacyIcon,
  PlusIcon,
  StarFilledIcon,
  UserCanceled2Icon,
  UserCanceledIcon,
  UserSixIcon
} from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { unAffectBlockedPlace } from '../../../api/blocked-places/delete-blocked-place'
import { assignEnrollmentsToCenterGroup } from '../../../api/centers/center-groups'
import { removeCenterEnrollments } from '../../../api/centers/remove-center-enrollments'
import AccomodationTooltip from '../../../components/elements/accomodation-tooltip'
import CseIdentifierTooltip from '../../../components/elements/cse-identifier-tooltip'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../../components/elements/information-tooltip'
import PeriodBadge from '../../../components/elements/period-badge'
import { AssociateBlockedPlace } from '../../../components/pages/centers/blocked-places/associate-blocked-place'
import { CenterEnrollmentsFilter } from '../../../components/pages/centers/center-enrollments/center-enrollments-filter'
import AssignCenterGroupForm from '../../../components/pages/centers/center-groups/assign-center-group-form'
import AssignCenterGroupCell from '../../../components/pages/ol-common/assign-center-group-cell'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import { MedicalConditionEnum } from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { friendPlaceEnumMapping } from '../../../lib/types/enums/friend/friend-place.enum'
import { BlockedPlaceLight } from '../../../lib/types/models/blocked-place/blocked-place'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { EnrollmentLight } from '../../../lib/types/models/enrollment/enrollment-light'
import { EnrollmentCenter } from '../../../lib/types/models/enrollment-center/enrollment-center'
import { JourneyProperties } from '../../../lib/types/models/product/journey.properties'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformEnrollmentsFiltersToUrl,
  transformUrlToEnrollmentFilters
} from '../../../lib/utils/filters-mapping/enrollment'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { getEnrollmentDocumentsStatus, mapEnrollmentsLightToEnrollmentDocument } from '../utils'
import EnrollmentDocumentStatusTooltip from './enrollment-document-status-tooltip'

// how two students get linked: only from a held place (see card 296), declared friends never create a link
const FRIENDS_HEADER_INFO = (
  <InformationToolTip
    variant="secondary"
    size="small"
    contentVariant="secondary"
    contentClassName="max-w-sm"
  >
    {
      "Pour associer deux jeunes : 1. bloquez d'abord une place pour l'ami (bouton « Bloquer une place ») ; 2. une fois l'ami affecté au centre, cliquez sur l'étiquette grise puis sur « Associer ». Les amis déclarés sont donnés par la famille à titre indicatif : ils ne créent aucun lien."
    }
  </InformationToolTip>
)

type Props = {
  centerId: number
  onReserveBlockedPlace: () => void
  onUnblockStudent: () => void
}

const CenterAffectedStudents: React.FC<Props> = ({
  centerId,
  onReserveBlockedPlace,
  onUnblockStudent
}) => {
  const tableRef = useRef<DynamicTableRef<EnrollmentCenter>>(null)
  const { navigateWithEvent, navigate } = useNavigator()
  const subject = useObserver({
    key: 'affected-students',
    update: () => tableRef.current?.refetch()
  })
  const { t } = useTranslate()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'remove-enrollments' | 'assign-group',
    EnrollmentLight[]
  >(['filters', 'remove-enrollments', 'assign-group'])

  type BlockedFriendWithChildInfo = Pick<BlockedPlaceLight, 'id' | 'firstName' | 'lastName'> & {
    enrollmentId: number
    childFirstName?: string
    childLastName?: string
  }

  const {
    selectedData: selectedFriend,
    modalState: friendModalState,
    toggleModal: friendToggleModal,
    closeModel: friendCloseModal
  } = useModalManager<'associate-friend' | 'deassociate-friend', BlockedFriendWithChildInfo>([
    'associate-friend',
    'deassociate-friend'
  ])

  const [isTableLoading, setIsTableLoading] = useState(false)
  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EnrollmentFilter>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformEnrollmentsFiltersToUrl,
      transformUrlToFilter: transformUrlToEnrollmentFilters
    })

  const {
    mutateAsync: removeCenterEnrollmentsMutation,
    isPending: isRemoveCenterEnrollmentsPending
  } = useMutation({
    mutationFn: removeCenterEnrollments,
    onSuccess: ({ message }) => {
      toast.success(message)
      tableRef.current?.refetch()
      subject.notify(['blocked-places'])
      onUnblockStudent()
      closeModel('remove-enrollments')
    },
    onError: showAxiosError
  })

  const {
    mutateAsync: unAffectBlockedPlaceCenterMutation,
    isPending: isUnAffectBlockedPlaceCenterPending
  } = useMutation({
    mutationFn: unAffectBlockedPlace,
    onSuccess: () => {
      tableRef.current?.refetch()
      subject.notify(['blocked-places'])
      onUnblockStudent()
      friendCloseModal('deassociate-friend')
    },
    onError: showAxiosError
  })

  const { mutateAsync: assignEnrollmentsMutation, isPending: isAssignEnrollmentsPending } =
    useMutation({
      mutationFn: assignEnrollmentsToCenterGroup,
      onSuccess: () => {
        tableRef.current?.refetch()
        closeModel('assign-group')
      },
      onError: showAxiosError
    })

  const columns: Column<EnrollmentCenter>[] = [
    {
      accessorKey: 'enrollment.mainProposition.journey.reference',
      header: 'Séjours',
      cell: ({ row }) => {
        const journey = row.original.enrollment.mainProposition?.journey
        if (!journey) return '-'
        return (
          <PeriodBadge
            period={journey.date.period}
            text={JourneyProperties.getJourneyLabel(journey)}
          />
        )
      }
    },
    {
      accessorKey: 'enrollment.mainProposition.options',
      header: `Options`,
      cell: ({ row }) => {
        const options = row.original.enrollment.mainProposition.options
        if (!options || options.length === 0) return '-'
        return options.map(({ option }) => getTranslatedName(option)).join(', ')
      }
    },
    { accessorKey: 'enrollment.enrollmentNumber', header: `N° d'inscription` },
    {
      accessorKey: 'enrollment.childLastName',
      header: 'NOM',
      cell: ({ row }) => {
        const lastName = row.original.enrollment.childLastName
        return <div className="text-balance py-1">{lastName?.toUpperCase()}</div>
      }
    },
    {
      accessorKey: 'enrollment.childFirstName',
      header: 'Prénom',
      cell: ({ row }) => {
        const firstName = row.original.enrollment.childFirstName
        return (
          <div className="text-balance py-1">
            {firstName ? StringFormatter.capitalize(firstName) : '-'}
          </div>
        )
      }
    },
    {
      accessorKey: 'enrollment.childBirthDate',
      header: 'Âge',
      enableSorting: true,
      invertSorting: true,
      cell: ({ row }) => EnrollmentProperties.getAgeAtDeparture(row.original.enrollment) ?? '-'
    },
    {
      accessorKey: 'enrollment.childGender',
      header: 'Sexe',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.enrollment.childGender
          ? genderOneLetterMapping[row.original.enrollment.childGender].label
          : '-'
    },
    {
      accessorKey: 'enrollment.childSchoolLevel',
      header: 'Classe',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.enrollment.childSchoolLevel
          ? schoolLevelEnumMapping[row.original.enrollment.childSchoolLevel]
          : '-'
    },
    {
      accessorKey: 'enrollment.isAA',
      header: 'AA',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.enrollment.isAA ? <StarFilledIcon width="24px" height="24px" /> : '-'
    },
    {
      accessorKey: 'enrollment.medicalLevel',
      header: 'Santé',
      enableSorting: true,
      cell: ({ row }) => {
        if (row.original.enrollment.medicalLevel === MedicalConditionEnum.RAS) return '-'
        return (
          <Badge
            variant="success"
            icon={
              <PharmacyIcon
                color={tailwindColors.green.DEFAULT}
                color2={tailwindColors.green.light.DEFAULT}
              />
            }
          />
        )
      }
    },
    {
      accessorKey: 'centerGroup.name',
      header: 'Sous-groupe',
      enableSorting: true,
      cell: ({ row }) => (
        <AssignCenterGroupCell
          permissions={['ASSIGN_CENTER_GROUP', 'UNASSIGN_CENTER_GROUP']}
          enrollmentCenter={row.original}
          onSuccess={() => tableRef.current?.refetch()}
        />
      )
    },
    {
      accessorKey: 'enrollment.blockedFriends',
      header: 'Amis associés',
      headerInfo: FRIENDS_HEADER_INFO,
      cell: ({ row }) => (
        <div className="flex gap-2 flex-col my-2">
          {row.original.enrollment.blockedFriends?.map((blockedFriend) => (
            <Badge
              key={blockedFriend.id}
              variant={blockedFriend.affectedEnrollmentId ? 'blue' : 'grey'}
              size="small"
              text={`${blockedFriend.firstName} ${blockedFriend.lastName}${blockedFriend.type ? ` - ${t(friendPlaceEnumMapping[blockedFriend.type].label)}` : ''}`}
              onClick={() => {
                // Check if the friend is already associated
                const blockedFriendWithChild: BlockedFriendWithChildInfo = {
                  ...blockedFriend,
                  enrollmentId: row.original.enrollment.id,
                  childFirstName: row.original.enrollment.childFirstName,
                  childLastName: row.original.enrollment.childLastName
                }
                if (blockedFriend.affectedEnrollmentId)
                  friendToggleModal('deassociate-friend', blockedFriendWithChild)
                else friendToggleModal('associate-friend', blockedFriendWithChild)
              }}
            />
          ))}
          {row.original.enrollment.blockedByFriends?.map((blockedByFriend) => (
            <Badge
              key={`blocked-by-${blockedByFriend.id}`}
              variant="blue"
              size="small"
              text={`${blockedByFriend.originalEnrollment?.childFirstName ?? ''} ${blockedByFriend.originalEnrollment?.childLastName ?? ''}${blockedByFriend.type ? ` - ${t(friendPlaceEnumMapping[blockedByFriend.type].label)}` : ''}`}
              onClick={() =>
                // the link lives on the other student's held place: unlinking it here removes it on both sides
                friendToggleModal('deassociate-friend', {
                  id: blockedByFriend.id,
                  firstName: blockedByFriend.originalEnrollment?.childFirstName,
                  lastName: blockedByFriend.originalEnrollment?.childLastName ?? '',
                  enrollmentId: row.original.enrollment.id,
                  childFirstName: row.original.enrollment.childFirstName,
                  childLastName: row.original.enrollment.childLastName
                })
              }
            />
          ))}
        </div>
      )
    },
    {
      accessorKey: 'enrollment.declaredFriends',
      header: 'Amis déclarés',
      headerInfo: FRIENDS_HEADER_INFO,
      cell: ({ row }) => (
        <div className="flex gap-2 flex-col my-2">
          {row.original.enrollment.declaredFriends?.map((declaredFriend) => (
            <Typography key={declaredFriend.id} variant="secondary-body">
              {`${toFullName(declaredFriend.firstName, declaredFriend.lastName, { options: { mode: 'lastname-firstname' } })} - ${friendPlaceEnumMapping[declaredFriend.type].title}`}
            </Typography>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'enrollment.cseRegistration.cse.identifier',
      header: 'CSE',
      cell: ({ row }) => (
        <CseIdentifierTooltip cse={row.original.enrollment.cseRegistration?.cse} size="small" />
      )
    },
    {
      accessorKey: 'enrollment.mainProposition.mainTrip',
      header: 'Voyage principal',
      cell: ({ row }) =>
        TripProperties.getTripLabel(row.original.enrollment.mainProposition.mainTrip)
    },
    {
      accessorKey: 'enrollment.mainProposition.secondaryTrip',
      header: 'Voyage secondaire',
      cell: ({ row }) =>
        row.original.enrollment.mainProposition.secondaryTrip
          ? TripProperties.getTripLabel(row.original.enrollment.mainProposition.secondaryTrip)
          : '-'
    },
    {
      accessorKey: 'hostFamily.parent1LastName',
      header: 'Hébergement',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.hostFamily ? (
          <Typography.Link
            variant="label-semibold"
            onClick={(e) => {
              e.stopPropagation()
              if (!row.original.hostFamily) return
              navigate(
                Paths.OL_HOST_FAMILY_INFORMATION.replace(
                  ':id',
                  row.original.hostFamily.id.toString()
                )
              )
            }}
          >
            {toFullName(
              row.original.hostFamily.parent1FirstName,
              row.original.hostFamily.parent1LastName,
              { options: { mode: 'lastname-firstname' } }
            )}
          </Typography.Link>
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'enrollment.mainProposition.familyArrangement',
      header: `Modalités d'accueil`,
      enableSorting: true,
      cell: ({ row }) => (
        <AccomodationTooltip
          familyArrangement={row.original.enrollment.mainProposition.familyArrangement}
        />
      )
    },
    {
      accessorKey: 'enrollment.id',
      header: 'Documents',
      cell: ({ row }) => (
        <div className="flex gap-2 my-2">
          {getEnrollmentDocumentsStatus(
            mapEnrollmentsLightToEnrollmentDocument(row.original.enrollment)
          ).map((status) => (
            <EnrollmentDocumentStatusTooltip
              key={status.label}
              enrollmentDocumentStatus={status}
            />
          ))}
        </div>
      )
    }
  ]

  return (
    <InnerBox className="rounded-3xl p-0">
      <DynamicDataTable<EnrollmentCenter, EnrollmentFilter>
        ref={tableRef}
        headerType="full"
        columns={columns}
        disableRow={(row) => row.center.locked}
        endpoint={constructApiUrl(API.centers.getEnrollments.url, { id: centerId })}
        filters={selectedFilter}
        title="Jeunes Affectés"
        selectable
        label={() => null}
        filtersLength={countFilters(selectedFilter)}
        onLoadingChange={setIsTableLoading}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(enrollmentCenter, event) =>
          navigateWithEvent(
            Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', enrollmentCenter.enrollmentId.toString()),
            event
          )
        }
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            variant: 'destructive',
            icon: UserCanceledIcon,
            label: 'Retirer du centre',
            fn: (data) =>
              toggleModal(
                'remove-enrollments',
                data.map(({ enrollment }) => enrollment)
              )
          },
          {
            type: 'multiple',
            variant: 'ghost',
            icon: PlusIcon,
            label: 'Assigner à un sous-groupe',
            fn: (data) =>
              toggleModal(
                'assign-group',
                data.map(({ enrollment }) => enrollment)
              )
          }
        ]}
      />
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <CenterEnrollmentsFilter
          centerId={centerId}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      </FilterDrawer>

      <Modal
        icon={UserCanceledIcon}
        variant={'destructive'}
        isOpen={modalState['remove-enrollments']}
        onCancel={() => closeModel('remove-enrollments')}
        title={`Êtes-vous sûr de vouloir retirer ${selectedData && selectedData.length > 1 ? `ces ${selectedData.length} jeunes` : 'ce jeune'} du centre ?`}
        isLoading={isRemoveCenterEnrollmentsPending}
        actionButtonsOptions={{
          confirmText: 'Retirer',
          cancelText: 'Annuler',
          onConfirm: () => {
            if (selectedData)
              removeCenterEnrollmentsMutation({
                id: centerId,
                enrollmentIds: selectedData.map((enrollment) => enrollment.id)
              })
          },
          hideIcon: true
        }}
      />

      <Modal
        icon={UserSixIcon}
        variant={'regular'}
        size="medium"
        isOpen={friendModalState['associate-friend']}
        onCancel={() => friendCloseModal('associate-friend')}
        titleAlignment="left"
        title={`Associer l'inscrit ${selectedFriend?.childFirstName ? StringFormatter.capitalize(selectedFriend.childFirstName) : ''} ${selectedFriend?.childLastName ? selectedFriend.childLastName.toUpperCase() : ''} avec l'ami associé ${selectedFriend?.firstName ? StringFormatter.capitalize(selectedFriend.firstName) : ''} ${selectedFriend?.lastName ? selectedFriend.lastName.toUpperCase() : ''}`}
        hideActions
      >
        {selectedFriend && (
          <AssociateBlockedPlace
            className={MODAL_BODY_CLASSNAME}
            blockedPlaceId={selectedFriend.id}
            centerId={centerId}
            enrollmentId={selectedFriend.enrollmentId}
            onSucceed={() => {
              tableRef.current?.refetch()
              subject.notify(['blocked-places'])
              friendCloseModal('associate-friend')
              onReserveBlockedPlace()
            }}
            onCancel={() => friendCloseModal('associate-friend')}
          />
        )}
      </Modal>

      <Modal
        icon={UserCanceled2Icon}
        variant={'confirmation'}
        isOpen={friendModalState['deassociate-friend']}
        onCancel={() => friendCloseModal('deassociate-friend')}
        title={`Souhaitez-vous dissocier ${selectedFriend?.firstName ? StringFormatter.capitalize(selectedFriend.firstName) : ''} ${selectedFriend?.lastName ? selectedFriend.lastName.toUpperCase() : ''} de ${selectedFriend?.childFirstName ? StringFormatter.capitalize(selectedFriend.childFirstName) : ''} ${selectedFriend?.childLastName ? selectedFriend.childLastName.toUpperCase() : ''} ?`}
        isLoading={isUnAffectBlockedPlaceCenterPending}
        actionButtonsOptions={{
          confirmText: 'Valider',
          cancelText: 'Annuler',
          onConfirm: async () => {
            if (selectedFriend)
              await unAffectBlockedPlaceCenterMutation({
                centerId,
                blockedPlaceId: selectedFriend.id
              })
          }
        }}
      />

      <Modal
        icon={PlusIcon}
        variant={'regular'}
        isOpen={modalState['assign-group']}
        onCancel={() => closeModel('assign-group')}
        title={'Assigner à un sous-groupe'}
        hideActions
      >
        {selectedData && (
          <AssignCenterGroupForm
            className={MODAL_BODY_CLASSNAME}
            centerId={centerId}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('assign-group'),
              isLoading: isAssignEnrollmentsPending,
              onConfirm: async (values) => {
                await assignEnrollmentsMutation({
                  centerId,
                  groupId: values.groupId,
                  ids: selectedData.flatMap((enrollment) =>
                    enrollment.enrollmentCenter?.id !== undefined
                      ? [enrollment.enrollmentCenter.id]
                      : []
                  )
                })
              }
            })}
          />
        )}
      </Modal>
    </InnerBox>
  )
}

export default CenterAffectedStudents
